import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SEOHead({ seo = {}, general = {} }) {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/faqs`).then(r => setFaqs(r.data)).catch(() => {});
  }, []);

  const title = seo.meta_title || "Campus Verano Activo La Estanca 2026 | Campamento para Niños en Aragón";
  const description = seo.meta_description || "Campamento de verano en La Estanca 2026 para niños de 6 a 13 años. Kayak, Paddle Surf, BTT, Orientación y HADO Realidad Aumentada. ¡Plazas limitadas!";
  const keywords = seo.meta_keywords || "campamento verano niños, campus verano La Estanca, actividades acuáticas niños";
  const siteName = seo.site_name || "Campus Verano Activo La Estanca 2026";
  const ogImage = seo.og_image || general.logo_url || "";
  const canonical = seo.canonical_url || (typeof window !== "undefined" ? window.location.origin : "");

  // ── Event Schema ─────────────────────────────────────────────────────────
  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": siteName,
    "description": description,
    "startDate": "2026-06-22",
    "endDate": "2026-07-03",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "La Estanca",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Alcañiz",
        "addressRegion": "Aragón",
        "addressCountry": "ES"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": general.organization || "Asociación MuéveTe",
      "telephone": `+34${general.whatsapp || "657871824"}`,
      "email": general.email || "veranoactivomuevete@gmail.com",
      "url": canonical
    },
    "offers": {
      "@type": "Offer",
      "price": "140",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/LimitedAvailability",
      "url": canonical,
      "validFrom": "2026-01-01"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Niños de 6 a 13 años"
    },
    "typicalAgeRange": "6-13",
    "isAccessibleForFree": false,
    ...(ogImage ? { "image": ogImage } : {}),
  };

  // ── FAQPage Schema ────────────────────────────────────────────────────────
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.pregunta,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.respuesta
      }
    }))
  } : null;

  // ── Organization Schema ───────────────────────────────────────────────────
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": general.organization || "Asociación MuéveTe",
    "url": canonical,
    "telephone": `+34${general.whatsapp || "657871824"}`,
    "email": general.email || "veranoactivomuevete@gmail.com",
    "sameAs": [],
    ...(ogImage ? { "logo": ogImage } : {}),
  };

  return (
    <Helmet>
      {/* Idioma */}
      <html lang="es" />

      {/* Básico */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content={general.organization || "Asociación MuéveTe"} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={seo.og_locale || "es_ES"} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:alt" content={siteName} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {seo.twitter_handle && <meta name="twitter:site" content={seo.twitter_handle} />}

      {/* Mobile / App */}
      <meta name="theme-color" content="#0072A4" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content="Campus Verano Activo" />

      {/* JSON-LD: Event */}
      <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>

      {/* JSON-LD: Organization */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      {/* JSON-LD: FAQPage */}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
}
